import { useEffect, useId, useMemo, useState } from "react";
import { createPhoneCall } from "../../services/conversation_service";
import { FiPhone } from "react-icons/fi";
import { Input, Modal } from "@iq/models";
import { BsTelephoneOutbound } from "react-icons/bs";
import { BsTelephoneInbound } from "react-icons/bs";
import { PHONE_CALL_OUTCOMES } from "../../models/phoneCallOutcomes";
import useLookupStore from "../../states/useLookupStore";
 
function AddPhoneCall({ isOpen, onClose, deals, contacts, contactId, onCreated, title = "Add Phone Call" }) {
    const formId = useId();
    const [direction, setDirection] = useState("outbound");
    const [callType, setCallType] = useState("Call");
    const [outcome, setOutcome] = useState("");
    const [durationSeconds, setDurationSeconds] = useState("");
    const [notes, setNotes] = useState("");
    const [callAt, setCallAt] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const accounts = useLookupStore((state) => state.accounts);
    const accountsError = useLookupStore((state) => state.accountsError);
    const hydrateAccounts = useLookupStore((state) => state.hydrateAccounts);
    const [selectedContactId, setSelectedContactId] = useState(null);
    const [selectedAccountId, setSelectedAccountId] = useState(null);

    const types = [
        "Call",
        "Voicemail",
        "Power Dialer",
    ]
    useEffect(() => {
        if (!isOpen) {
            setDirection("outbound");
            setCallType("Call");
            setOutcome("");
            setDurationSeconds("");
            setNotes("");
            setCallAt("");
            setError("");
            setSaving(false);
        }
    }, [isOpen]);

    const normalizedDeals = useMemo(() => {
        return Array.isArray(deals) ? deals : [];
    }, [deals]);

    const normalizedContacts = useMemo(() => {
        return Array.isArray(contacts) ? contacts : [];
    }, [contacts]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const defaultContact = contactId != null
            ? normalizedContacts.find((contact) => String(contact.id) === String(contactId))
            : normalizedContacts[0];
        setSelectedContactId(defaultContact?.id || null);
        setSelectedAccountId(defaultContact?.account_id || defaultContact?.accountId || null);
    }, [contactId, isOpen, normalizedContacts]);

    useEffect(() => {
        if (isOpen) {
            hydrateAccounts();
        }
    }, [hydrateAccounts, isOpen]);

    const selectedContact = useMemo(() => {
        return normalizedContacts.find((contact) => String(contact.id) === String(selectedContactId)) || null;
    }, [normalizedContacts, selectedContactId]);

    const selectedAccount = useMemo(() => {
        return accounts.find((account) => String(account.id) === String(selectedAccountId)) || null;
    }, [accounts, selectedAccountId]);

    const selectedDeal = useMemo(() => {
        if (!selectedContact) {
            return null;
        }

        const selectedContactId = Number.parseInt(String(selectedContact.id || "0"), 10);
        const matchedDeal = normalizedDeals.find((deal) => Number(deal.contact_id || 0) === selectedContactId);
        return matchedDeal || null;
    }, [normalizedDeals, selectedContact]);

    const actionButtons = useMemo(() => ([
        {
            key: "log-call",
            text: saving ? "Logging..." : "Log Call",
            type: "submit",
            form: formId,
            disabled: saving || !outcome || (!selectedContactId && !selectedAccountId),
            color: "accent",
        },
    ]), [formId, outcome, saving, selectedAccountId, selectedContactId]);

    if (!isOpen) {
        return null;
    }

    async function handleSubmit(event) {
        event.preventDefault();
        setError("");
        setSaving(true);

        const parsedDuration = Number.parseInt(durationSeconds || "0", 10);
        const parsedContactId = Number.parseInt(String(selectedContact?.id || "0"), 10);
        const parsedAccountId = Number.parseInt(String(selectedAccountId || "0"), 10);
        const selectedDealId = Number.parseInt(String(selectedDeal?.id || "0"), 10);

        if (!parsedContactId && !parsedAccountId) {
            setError("Select an account or contact for this call.");
            setSaving(false);
            return;
        }

        try {
            const payload = await createPhoneCall({
                deal_id: selectedDealId > 0 ? selectedDealId : undefined,
                account_id: parsedAccountId > 0 ? parsedAccountId : undefined,
                contact_id: parsedContactId > 0 ? parsedContactId : undefined,
                direction,
                call_type: callType,
                outcome,
                duration_seconds: Number.isFinite(parsedDuration) && parsedDuration > 0 ? parsedDuration : 0,
                notes: notes.trim() || undefined,
                call_at: callAt ? new Date(callAt).toISOString() : undefined,
            });

            onCreated?.(payload);
            onClose?.();
        } catch (err) {
            setError(err?.message || "Failed to create phone call");
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            icon={<FiPhone />}
            actionButtons={actionButtons}
            errorMessage={error || accountsError}
        >
                <form id={formId} className="modal-form-container" onSubmit={handleSubmit}>
                    <div className="form-fields-container">
                        <div className="modal-form-field">
                            <label htmlFor="phone-call-contact">Contact</label>
                            <Input
                                type="dropdown"
                                id="phone-call-contact"
                                placeholder="Account only"
                                value={selectedContact?.name || "Account only"}
                                allowCustom={false}
                                colorScheme="light"
                                options={[
                                    { value: "", label: "Account only" },
                                    ...normalizedContacts.map((contact) => ({ value: contact.id, label: contact.name })),
                                ]}
                                onOptionSelect={(option) => {
                                    const contact = normalizedContacts.find((item) => String(item.id) === String(option.value));
                                    setSelectedContactId(contact?.id || null);
                                    setSelectedAccountId(contact?.account_id || contact?.accountId || selectedAccountId);
                                }}
                            />
                        </div>

                        <div className="modal-form-field">
                            <label htmlFor="phone-call-account">Account</label>
                            <Input
                                type="dropdown"
                                id="phone-call-account"
                                placeholder="Select account"
                                value={selectedAccount?.name || selectedContact?.account || ""}
                                allowCustom={false}
                                colorScheme="light"
                                options={accounts.map((account) => ({ value: account.id, label: account.name }))}
                                onOptionSelect={(option) => {
                                    setSelectedAccountId(option.value);
                                    const contactAccountId = selectedContact?.account_id || selectedContact?.accountId;
                                    if (contactAccountId && String(contactAccountId) !== String(option.value)) {
                                        setSelectedContactId(null);
                                    }
                                }}
                            />
                        </div>

                        <div className="modal-form-field">
                            <label>Deal</label>
                            <div className="form-static-value">{selectedDeal?.title || "No deal"}</div>
                        </div>
                        <div className="modal-form-field">
                            <label htmlFor="phone-call-direction">Direction</label>
                            <div className="modal-form-options-2">
                                <div className={`modal-form-option-field ${direction === "inbound" ? "modal-form-option-field-active" : ""}`} onClick={() => setDirection("inbound")}>
                                     Inbound <BsTelephoneInbound />
                                </div>
                                <div className={`modal-form-option-field ${direction === "outbound" ? "modal-form-option-field-active" : ""}`} onClick={() => setDirection("outbound")}>
                                     Outbound <BsTelephoneOutbound />
                                </div>
                            </div>
                        </div>

                        <div className="modal-form-field">
                            <label htmlFor="phone-call-type">Type</label>
                            <Input
                                type="dropdown"
                                id="phone-call-type"
                                name="phone-call-type"
                                placeholder="Select a type"
                                value={callType}
                                allowCustom={false}
                                colorScheme="light"
                                ariaLabel="Call type options"
                                options={types.map((typeOption) => ({ value: typeOption, label: typeOption }))}
                                onOptionSelect={(option) => setCallType(option.value)}
                            />
                        </div>

                        <div className="modal-form-field">
                            <label htmlFor="phone-call-outcome">Outcome</label>

                            <Input
                                type="dropdown"
                                id="phone-call-outcome"
                                name="phone-call-outcome"
                                placeholder="Select an outcome"
                                value={PHONE_CALL_OUTCOMES.find((option) => option.value === outcome)?.label || ""}
                                allowCustom={false}
                                colorScheme="light"
                                ariaLabel="Call outcome options"
                                options={PHONE_CALL_OUTCOMES}
                                onOptionSelect={(option) => setOutcome(option.value)}
                            />
                        </div>

                        <div className="modal-form-field">
                            <label htmlFor="phone-call-duration">Duration (seconds)</label>
                            <Input
                                id="phone-call-duration"
                                type="number"
                                min="0"
                                placeholder="0"
                                value={durationSeconds}
                                onChange={(event) => setDurationSeconds(event.target.value)}
                                colorScheme="light"
                            />
                        </div>

                        <div className="modal-form-field">
                            <label htmlFor="phone-call-at">Call Time</label>
                            <Input
                                id="phone-call-at"
                                type="datetime-local"
                                value={callAt}
                                onChange={(event) => setCallAt(event.target.value)}
                                fieldClassName="modal-form-field-datetime"
                                colorScheme="light"
                            />
                        </div>
                        {/* Dummy field for spacing */}
                        <div className="modal-form-field"/>

                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="phone-call-notes">Conversation Notes</label>
                            <Input
                                id="phone-call-notes"
                                type="text"
                                placeholder="What happened on the call?"
                                value={notes}
                                onChange={(event) => setNotes(event.target.value)}
                                colorScheme="light"
                            />
                        </div>
                    </div>
                    
                </form>
        </Modal>
    );
}

export default AddPhoneCall;
